/* =========================================================================
   ClueVine — difficulty & anti-frustration
   Modes own their own difficulty ladders (ordered easy → hard). This module
   resolves a level safely and applies the "quietly ease after a rough run"
   rule: after a couple of consecutive losses at a level, the next puzzle steps
   down one notch. Combined with avoid-recent answers (seed.ts) and
   guaranteed-solvable generation in each mode, that's the anti-frustration kit.
   ========================================================================= */

import type { AnyPuzzleMode, DifficultyLevel } from './types';

/** Consecutive losses at a difficulty before the next puzzle eases down. */
export const STRUGGLE_THRESHOLD = 2;

/** Resolve a difficulty id to a level, falling back to the mode's default. */
export function resolveDifficulty(mode: AnyPuzzleMode, id: string): DifficultyLevel {
  return (
    mode.difficulties.find((d) => d.id === id) ??
    mode.difficulties.find((d) => d.id === mode.defaultDifficultyId) ??
    mode.difficulties[0]
  );
}

export interface Adaptation {
  difficulty: DifficultyLevel;
  /** True when the level was eased below what the player requested. */
  eased: boolean;
}

/**
 * Decide the difficulty to actually play, given the requested level and how
 * many times in a row the player has just lost at it. Never eases below the
 * easiest level, and never eases when the player isn't struggling.
 */
export function adaptDifficulty(
  mode: AnyPuzzleMode,
  requestedId: string,
  lossStreak: number,
): Adaptation {
  const requested = resolveDifficulty(mode, requestedId);
  const idx = mode.difficulties.findIndex((d) => d.id === requested.id);

  if (lossStreak >= STRUGGLE_THRESHOLD && idx > 0) {
    return { difficulty: mode.difficulties[idx - 1], eased: true };
  }
  return { difficulty: requested, eased: false };
}
